'use client';

import { useEffect } from 'react';

interface LegalModalProps {
  type: 'privacy' | 'terms' | 'refund' | 'delivery' | null;
  onClose: () => void;
}

const legalContent: Record<string, { title: string; content: React.ReactNode }> = {
  privacy: {
    title: 'Privacy Policy',
    content: (
      <div className="space-y-6 text-sm leading-relaxed" style={{ color: '#374151' }}>
        <p style={{ color: '#6B7280', fontSize: '12px' }}>Last updated: March 1, 2026 | CARTELIX LLC, Wyoming, USA</p>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>1. Information We Collect</h3>
          <p>We collect information you provide directly, including name, email address, company name, and project details submitted via our contact form. We may also collect usage data through cookies and analytics tools.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>2. How We Use Your Information</h3>
          <p>We use your information to respond to inquiries, deliver contracted services, process payments, and communicate project updates. We do not sell your personal data to third parties.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>3. Cookies</h3>
          <p>Our website uses essential cookies for functionality and optional analytics cookies (e.g., Google Analytics) to improve user experience. You may opt out of non-essential cookies via our cookie consent banner.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>4. Third-Party Processors</h3>
          <p>We use Stripe and PayPal to process payments. These processors operate under their own privacy policies and are PCI DSS compliant. We do not store your payment card information on our systems. We may also use Google Workspace for internal communication.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>5. GDPR Compliance</h3>
          <p>If you are located in the European Economic Area (EEA), you have the right to access, rectify, or delete your personal data. You may also object to or restrict processing, and request data portability. To exercise these rights, contact us at contact@cartelix-llc.com.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>6. Data Security</h3>
          <p>We implement industry-standard security measures including SSL/TLS encryption, access controls, and secure hosting environments to protect your personal data from unauthorized access or disclosure.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>7. Data Retention</h3>
          <p>We retain client data for a maximum of 3 years following project completion, unless a longer retention period is required by law or mutually agreed upon.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>8. Contact</h3>
          <p>For privacy-related inquiries: contact@cartelix-llc.com | CARTELIX LLC, 30 N Gould St Ste R, Sheridan, WY 82801, USA</p>
        </div>
      </div>
    ),
  },
  terms: {
    title: 'Terms of Service',
    content: (
      <div className="space-y-6 text-sm leading-relaxed" style={{ color: '#374151' }}>
        <p style={{ color: '#6B7280', fontSize: '12px' }}>Last updated: March 1, 2026 | CARTELIX LLC, Wyoming, USA</p>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>1. Scope of Services</h3>
          <p>Cartelix LLC provides digital services including website creation, mobile application development, social media content design, and brand identity work. The specific scope of each engagement is defined in a written project proposal accepted by both parties prior to commencement.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>2. Payment Terms</h3>
          <p>All projects require a 50% deposit payment before work begins. The remaining 50% balance is due upon client approval of final deliverables. All fees are quoted and invoiced in USD. Overdue balances beyond 14 days of invoice may incur a 1.5% monthly late fee.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>3. Intellectual Property</h3>
          <p>Upon receipt of full payment, the client receives full ownership of all deliverables produced under the engagement. Cartelix LLC retains no ongoing license to the deliverables. Third-party assets (fonts, stock photos, plugins) remain subject to their respective licenses.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>4. Revisions</h3>
          <p>Each project includes a defined number of revision rounds as specified in the proposal. Additional revision requests beyond the agreed scope will be quoted separately and billed at our standard hourly rate.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>5. Limitation of Liability</h3>
          <p>Cartelix LLC&apos;s total liability for any claim arising from a project shall not exceed the total fees paid by the client for that specific project. We are not liable for indirect, incidental, or consequential damages including lost profits or business interruption.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>6. Governing Law</h3>
          <p>These Terms of Service are governed by the laws of the State of Wyoming, United States of America. Any disputes shall be resolved in the courts of Sheridan County, Wyoming.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>7. Termination</h3>
          <p>Either party may terminate the engagement with 7 days written notice. In the event of client-initiated termination after work has commenced, the deposit is non-refundable and the client is liable for work completed to date.</p>
        </div>
      </div>
    ),
  },
  refund: {
    title: 'Refund Policy',
    content: (
      <div className="space-y-6 text-sm leading-relaxed" style={{ color: '#374151' }}>
        <p style={{ color: '#6B7280', fontSize: '12px' }}>Last updated: March 1, 2026 | CARTELIX LLC, Wyoming, USA</p>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>1. 14-Day Refund Window</h3>
          <p>Clients may request a full refund of the 50% deposit within 14 calendar days of payment, provided that no significant project work has commenced. A refund request must be submitted in writing to contact@cartelix-llc.com.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>2. Partial Refunds</h3>
          <p>If project work has commenced at the time of a refund request, a partial refund may be issued based on the percentage of work not yet completed. The refundable amount will be calculated and communicated within 5 business days of the request.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>3. Non-Refundable Items</h3>
          <p>The following are non-refundable under any circumstances: (a) completed and approved deliverables; (b) third-party licenses or subscriptions purchased on behalf of the client; (c) deposits on projects where work has been substantially completed; (d) final 50% payment once deliverables have been approved.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>4. Refund Processing</h3>
          <p>Approved refunds are processed within 7–10 business days to the original payment method. Stripe and PayPal processing fees are non-recoverable and will be deducted from the refund amount.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>5. Disputes</h3>
          <p>In the event of a dispute regarding deliverable quality, we will make reasonable efforts to resolve the issue through additional revisions before a refund is considered. All disputes must be raised within 7 days of deliverable submission.</p>
        </div>
      </div>
    ),
  },
  delivery: {
    title: 'Delivery Policy',
    content: (
      <div className="space-y-6 text-sm leading-relaxed" style={{ color: '#374151' }}>
        <p style={{ color: '#6B7280', fontSize: '12px' }}>Last updated: March 1, 2026 | CARTELIX LLC, Wyoming, USA</p>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>1. Digital Service Delivery</h3>
          <p>All Cartelix LLC services are delivered digitally. Physical delivery of goods is not applicable. Deliverables are transferred via secure file sharing platforms, email, or client-designated repositories.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>2. Estimated Timelines</h3>
          <ul className="list-none space-y-2 mt-2">
            <li>• <strong>Website Creation:</strong> 2–4 weeks from project kickoff</li>
            <li>• <strong>Mobile Applications:</strong> 6–12 weeks from project kickoff</li>
            <li>• <strong>Social Media Posts:</strong> 5–7 business days per monthly package</li>
            <li>• <strong>Rebranding:</strong> 2–3 weeks from project kickoff</li>
          </ul>
          <p className="mt-3">All timelines are estimates and are subject to the complexity of the project and client feedback response times. Exact timelines are confirmed in the written project proposal.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>3. Milestone-Based Delivery</h3>
          <p>Projects are delivered in milestones. Each milestone is presented to the client for review and approval before the next phase begins. Client feedback must be provided within 5 business days of each milestone submission to maintain the agreed timeline.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>4. Communication Process</h3>
          <p>All project communication is conducted via email (contact@cartelix-llc.com) and scheduled video calls. Clients are assigned a dedicated project contact. Response time for project-related queries is within 1 business day.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>5. Final Delivery</h3>
          <p>Final deliverables are released upon confirmation of full payment receipt. All source files, credentials, and documentation are transferred within 2 business days of final payment confirmation.</p>
        </div>

        <div>
          <h3 className="font-heading font-bold text-base mb-2" style={{ color: '#0D1A3A' }}>6. Post-Delivery Support</h3>
          <p>All projects include 30 days of complimentary post-delivery support for bug fixes and minor adjustments. Extended support agreements are available upon request.</p>
        </div>
      </div>
    ),
  },
};

export default function LegalModal({ type, onClose }: LegalModalProps) {
  useEffect(() => {
    if (type) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [type]);

  if (!type) return null;

  const content = legalContent[type];

  return (
    <div className="legal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="legal-modal">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-5" style={{ borderBottom: '1px solid #E5E9F5' }}>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#9CA3AF' }}>
              CARTELIX LLC — Legal Document
            </p>
            <h2 className="font-heading font-bold text-xl" style={{ color: '#0D1A3A' }}>
              {content.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
            style={{ background: '#F3F4F6' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#E5E7EB')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#F3F4F6')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        {content.content}

        {/* Footer */}
        <div className="mt-8 pt-5" style={{ borderTop: '1px solid #E5E9F5' }}>
          <p className="text-xs" style={{ color: '#9CA3AF' }}>
            CARTELIX LLC | 30 N Gould St Ste R, Sheridan, WY 82801, USA | contact@cartelix-llc.com
          </p>
        </div>
      </div>
    </div>
  );
}